import { useState } from 'react';
import axios from 'axios';
import { TERMS_PDF, PRIVACY_PDF } from '../utils/consent';

// First-login interstitial for bulk-imported users (must_change_password=1).
// They're already authenticated with their temp password — this screen makes
// them (a) choose their own password and (b) accept the SHIFTon T&Cs and
// Privacy Policy, which self-registered users do at registration but imported
// users never saw. POST /api/auth/set-first-password does both atomically and
// returns a fresh token without the lock flag; every other API route is 403
// until then. Legal note: the documents are SHIFTon's (hardcoded, NOT the
// white-label brand) — SHIFTon is the Responsible Party regardless of which
// agency imported the staff member. Same rule as AuthPage.
//
// Session W (P3): the PDF filenames carry the version consent_log records.
// Unversioned names meant replacing either PDF rewrote the document every past
// consent record pointed at. A new version is a NEW file.
// Session X: the version lives in utils/consent.js, kept in sync with the
// TERMS_VERSION / PRIVACY_VERSION constants stamped into the consent INSERT
// in server.js.
export default function SetPassword() {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 8) { setError('Password must be at least 8 characters.'); return; }
    if (password !== confirm) { setError('Passwords do not match. Please try again.'); return; }
    if (!agreed) { setError('Please accept the Terms & Conditions and Privacy Policy to continue.'); return; }
    setSaving(true);
    try {
      const { data } = await axios.post('/api/auth/set-first-password', {
        password,
        agreed_to_terms: true,
      });
      // Swap in the fresh token (no lock flag) and reload — the app boots
      // normally, /me no longer carries must_change_password.
      localStorage.setItem('es_token', data.token);
      window.location.replace('/');
    } catch (err) {
      setError(err.response?.data?.error || 'Could not set your password. Please try again.');
      setSaving(false);
    }
  };

  return (
    <div className="auth-screen">
      <div className="auth-box">
        <div className="auth-logo">
          <h1>
            <span style={{color:'var(--text-primary)',fontWeight:800}}>{process.env.REACT_APP_BRAND_PREFIX||'SHIFT'}</span>
            <span style={{color:'var(--accent)',fontWeight:800}}>{process.env.REACT_APP_BRAND_SUFFIX||'on'}</span>
          </h1>
        </div>

        <div style={{textAlign:'center',marginBottom:20}}>
          <div style={{fontSize:32,marginBottom:10}}>🔐</div>
          <div style={{fontWeight:700,fontSize:17,marginBottom:8,color:'var(--text-primary)'}}>Welcome! Set your password</div>
          <div style={{fontSize:13,color:'var(--text-secondary)',lineHeight:1.6}}>
            Your account was set up by your agency with a temporary password.
            Choose your own password to continue — you'll use it every time you log in.
          </div>
        </div>

        <form onSubmit={submit}>
          <div className="form-group">
            <label className="form-label">New Password</label>
            <input className="form-input" type="password" placeholder="Min. 8 characters"
              value={password} onChange={e => setPassword(e.target.value)} required minLength={8} />
          </div>
          <div className="form-group">
            <label className="form-label">Confirm New Password</label>
            <input className="form-input" type="password" placeholder="Re-enter your new password"
              value={confirm} onChange={e => setConfirm(e.target.value)} required minLength={8} />
          </div>

          <div style={{display:'flex',alignItems:'flex-start',gap:10,margin:'16px 0',padding:'12px 14px',background:'var(--bg-surface)',border:'1px solid var(--border)',borderRadius:8}}>
            <input
              type="checkbox"
              id="terms-checkbox"
              checked={agreed}
              onChange={e => setAgreed(e.target.checked)}
              style={{marginTop:2,accentColor:'var(--accent)',cursor:'pointer',flexShrink:0,width:16,height:16}}
            />
            <label htmlFor="terms-checkbox" style={{fontSize:12,color:'var(--text-secondary)',lineHeight:1.6,cursor:'pointer'}}>
              I have read and agree to the{' '}
              <a href={TERMS_PDF} target="_blank" rel="noopener noreferrer" style={{color:'var(--accent)',fontWeight:600,textDecoration:'underline'}}>Terms &amp; Conditions</a>
              {' '}and{' '}
              <a href={PRIVACY_PDF} target="_blank" rel="noopener noreferrer" style={{color:'var(--accent)',fontWeight:600,textDecoration:'underline'}}>Privacy Policy</a>
              {' '}of SHIFTon.
            </label>
          </div>

          {error && <p style={{color:'var(--danger)',fontSize:13,marginBottom:12}}>{error}</p>}

          <button className="btn btn-primary" type="submit" disabled={saving}>
            {saving ? 'Saving...' : 'Set Password & Continue'}
          </button>
        </form>

        <div className="auth-switch">
          Wrong account?{' '}
          <button type="button" onClick={() => { localStorage.removeItem('es_token'); window.location.reload(); }}>
            Sign Out
          </button>
        </div>
      </div>
    </div>
  );
}
